import { type GuestDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { roomLabel } from '../../shared/format.js';
import { useQuickFilter } from '../../shared/hooks/useQuickFilter.js';
import { GuestAvatar } from '../../shared/illustrations/GuestAvatar.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorAlert,
  FilterInput,
  LoadingBlock,
  PageHeader,
} from '../../shared/ui/index.js';
import { useAllRooms } from '../houses/houseHooks.js';
import { GuestDialog } from './GuestDialog.js';
import { useDeleteGuest, useGuests } from './guestHooks.js';

export function GuestsPage(): ReactElement {
  const t = useTranslation();
  const guests = useGuests();
  const deleteGuest = useDeleteGuest();
  const rooms = useAllRooms();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<GuestDto | null>(null);
  const [activeTags, setActiveTags] = useState<string[]>([]);

  const allTags = [...new Set((guests.data ?? []).flatMap((guest) => guest.tags))].sort();
  // A guest must carry every tag currently toggled on, not merely one of them.
  const byTag = (guests.data ?? []).filter((guest) =>
    activeTags.every((tag) => guest.tags.includes(tag)),
  );

  // Traits are searchable by their label, so "reduced" finds everyone with
  // reduced mobility without the user knowing the internal option name.
  const filter = useQuickFilter(byTag, (guest) => [
    guest.name,
    guest.notes,
    ...guest.options.map((option) => t.catalog.guestOption[option].label),
    ...guest.tags,
  ]);

  const toggleTag = (tag: string): void => {
    setActiveTags(
      activeTags.includes(tag) ? activeTags.filter((item) => item !== tag) : [...activeTags, tag],
    );
  };

  const nameOf = (guestId: string): string =>
    guests.data?.find((guest) => guest.id === guestId)?.name ?? t.guestsPage.someone;
  const labelOfRoom = (roomId: string): string => {
    const room = rooms.data?.find((candidate) => candidate.id === roomId);
    return room === undefined ? t.guestsPage.aRoom : roomLabel(room);
  };

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={t.guestsPage.title}
        subtitle={t.guestsPage.subtitle}
        actions={<Button onClick={() => setCreating(true)}>{t.guestsPage.addGuest}</Button>}
      />

      <ErrorAlert error={guests.error ?? deleteGuest.error} />

      {guests.isPending ? <LoadingBlock /> : null}

      {guests.data !== undefined && guests.data.length > 0 ? (
        <FilterInput
          label={t.guestsPage.filterLabel}
          placeholder={t.guestsPage.filterPlaceholder}
          value={filter.query}
          matchCount={filter.matchCount}
          totalCount={filter.totalCount}
          onChange={filter.setQuery}
        />
      ) : null}

      {allTags.length > 0 ? (
        <div className="option-list">
          {allTags.map((tag) => {
            const selected = activeTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                className={`option-chip${selected ? ' option-chip--selected' : ''}`}
                aria-pressed={selected}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            );
          })}
        </div>
      ) : null}

      {guests.data?.length === 0 ? (
        <EmptyState
          title={t.guestsPage.emptyTitle}
          description={t.guestsPage.emptyDescription}
          action={<Button onClick={() => setCreating(true)}>{t.guestsPage.addGuest}</Button>}
        />
      ) : null}

      {(guests.data?.length ?? 0) > 0 && filter.matchCount === 0 ? (
        <EmptyState
          title={t.guestsPage.noMatchesTitle}
          description={
            filter.isFiltering
              ? t.guestsPage.noMatchesForQuery(filter.query)
              : t.guestsPage.noMatchesForTags
          }
          action={
            <Button
              variant="secondary"
              onClick={() => {
                filter.setQuery('');
                setActiveTags([]);
              }}
            >
              {t.guestsPage.clearFilters}
            </Button>
          }
        />
      ) : null}

      {filter.matchCount > 0 ? (
        <div className="grid">
          {filter.results.map((guest) => (
            <Card key={guest.id}>
              <div className="stack stack--tight">
                <div className="row row--between">
                  <h2 className="card__title">
                    <GuestAvatar
                      guestId={guest.id}
                      name={guest.name}
                      gender={guest.gender}
                      size="sm"
                    />
                    {guest.name}
                  </h2>
                  <div className="row">
                    <Button small variant="secondary" onClick={() => setEditing(guest)}>
                      {t.common.edit}
                    </Button>
                    <ConfirmButton
                      small
                      label={t.common.delete}
                      onConfirm={() => deleteGuest.mutate(guest.id)}
                    />
                  </div>
                </div>

                {guest.options.length > 0 ? (
                  <div className="option-list">
                    {guest.options.map((option) => (
                      <Badge key={option} tone="accent">
                        {t.catalog.guestOption[option].label}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                {guest.tags.length > 0 ? (
                  <div className="option-list">
                    {guest.tags.map((tag) => (
                      <Badge key={tag}>{tag}</Badge>
                    ))}
                  </div>
                ) : null}

                {guest.roommateIds.length > 0 ? (
                  <p className="card__meta">
                    {t.guestsPage.wantsToRoomWith(guest.roommateIds.map(nameOf).join(', '))}
                  </p>
                ) : null}

                {guest.incompatibleGuestIds.length > 0 ? (
                  <p className="card__meta">
                    {t.guestsPage.neverWith(guest.incompatibleGuestIds.map(nameOf).join(', '))}
                  </p>
                ) : null}

                {guest.incompatibleRoomIds.length > 0 ? (
                  <p className="card__meta">
                    {t.guestsPage.refuses(guest.incompatibleRoomIds.map(labelOfRoom).join(', '))}
                  </p>
                ) : null}

                {guest.notes !== null ? <p className="card__meta">{guest.notes}</p> : null}
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {creating ? (
        <GuestDialog allGuests={guests.data ?? []} onClose={() => setCreating(false)} />
      ) : null}
      {editing !== null ? (
        <GuestDialog
          guest={editing}
          allGuests={guests.data ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
